import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { formatNumberFR, formatPriceFR } from '../utils/formatFR';
import ChiffresCles from '../components/observatoire/ChiffresCles';
import FiltresObservatoire from '../components/observatoire/FiltresObservatoire';
import GraphiqueFamilles from '../components/observatoire/GraphiqueFamilles';
import GraphiquePrix from '../components/observatoire/GraphiquePrix';
import GraphiqueTranches from '../components/observatoire/GraphiqueTranches';
import GraphiqueVentes from '../components/observatoire/GraphiqueVentes';
import TableauObservatoire from '../components/observatoire/TableauObservatoire';
import { COULEUR_PORTEFEUILLE } from '../components/observatoire/couleurs';

const COLONNE_SOUS_ZONE = { region: 'Région', departement: 'Département', commune: 'Commune' };

/** Observatoire — ventes de fonds de commerce publiées au BODACC (lot DI). */
export default function ObservatoirePage() {
  const [parametres, setParametres] = useSearchParams();
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const filtres = useMemo(() => ({
    zoneType: parametres.get('zone_type'),
    zone: parametres.get('zone'),
    profil: parametres.get('profil'),
    sousProfil: parametres.get('sous_profil'),
    mois: Number(parametres.get('mois')) || 12,
    portefeuille: parametres.get('portefeuille') === '1',
  }), [parametres]);

  const changerFiltres = useCallback((modifications) => {
    const suivant = { ...filtres, ...modifications };
    const prochains = {};
    if (suivant.zoneType && suivant.zone) {
      prochains.zone_type = suivant.zoneType;
      prochains.zone = suivant.zone;
    }
    if (suivant.profil) prochains.profil = suivant.profil;
    if (suivant.sousProfil) prochains.sous_profil = suivant.sousProfil;
    if (suivant.mois !== 12) prochains.mois = String(suivant.mois);
    if (suivant.portefeuille) prochains.portefeuille = '1';
    setParametres(prochains);
  }, [filtres, setParametres]);

  useEffect(() => {
    let annule = false;
    setChargement(true);
    setErreur(null);
    api.get('/observatoire', {
      params: {
        zone_type: filtres.zoneType || undefined,
        zone: filtres.zone || undefined,
        profil: filtres.profil || undefined,
        sous_profil: filtres.sousProfil || undefined,
        mois: filtres.mois,
        portefeuille: filtres.portefeuille ? 1 : 0,
      },
    })
      .then(({ data }) => { if (!annule) setDonnees(data); })
      .catch((e) => {
        if (annule) return;
        setErreur(e?.response?.status === 403
          ? "Le module Statistiques n'est pas activé pour votre agence."
          : "Les statistiques n'ont pas pu être chargées.");
      })
      .finally(() => { if (!annule) setChargement(false); });
    return () => { annule = true; };
  }, [filtres]);

  // Onglet du navigateur (une seule source pour l'instant)
  useEffect(() => {
    document.title = 'Cessions de fonds de commerce — Observatoire';
  }, []);

  const activiteChoisie = Boolean(filtres.profil || filtres.sousProfil);
  const portefeuille = filtres.portefeuille ? (donnees?.portefeuille || null) : null;
  const chr = filtres.profil === 'chr';

  if (erreur) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="observatoire-erreur">
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center">
          <i className="bi bi-exclamation-triangle text-2xl text-red-500"></i>
          <p className="text-sm text-slate-700 mt-2">{erreur}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-4 overflow-x-hidden text-left"
         data-testid="observatoire-page">
      {/* ═══════ En-tête — prévu pour recevoir des onglets par source (BODACC, DVF…) :
          tant qu'il n'y a qu'une source, rien n'est affiché. ═══════ */}
      <header className="min-w-0">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
          Cessions de fonds de commerce
        </h1>
        <p className="text-base text-slate-600">
          Ventes publiées au BODACC — hors murs et locations
        </p>
        <span className="inline-block mt-2 px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[11px] font-semibold"
              data-testid="observatoire-source">
          Source : {donnees?.source || 'BODACC — DILA, Licence Ouverte'}
        </span>
      </header>

      <FiltresObservatoire
        filtres={filtres}
        zoneLibelle={donnees?.zone?.libelle}
        onChanger={changerFiltres}
      />

      {/* ═══════ Bandeaux ═══════ */}
      <div className="bg-slate-100 rounded-xl p-3 text-xs text-slate-600 space-y-1"
           data-testid="observatoire-bandeau">
        <p>
          Fonds de commerce uniquement. Les ventes de murs et les locations ne sont pas publiées au
          BODACC. Les dates sont celles de la publication, quelques semaines après la vente.
        </p>
        {chr && (
          <p data-testid="observatoire-bandeau-chr">
            Le BODACC ne publie ni le nombre de couverts ou de chambres, ni la surface, ni le chiffre
            d'affaires : ces prix donnent un ordre de grandeur.
          </p>
        )}
      </div>
      {filtres.portefeuille && (
        <div className="rounded-xl p-3 text-xs border text-slate-700"
             style={{ borderColor: COULEUR_PORTEFEUILLE, backgroundColor: '#FFF5EF' }}
             data-testid="observatoire-bandeau-portefeuille">
          <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle"
                style={{ backgroundColor: COULEUR_PORTEFEUILLE }}></span>
          <strong className="font-semibold">Votre portefeuille</strong> : vos fonds de commerce en vente, au prix net vendeur (sans
          vos honoraires, comme au BODACC). Ce sont des prix demandés, pas des prix de vente. Visible
          par votre agence seulement.
        </div>
      )}

      {chargement && !donnees ? (
        <div className="py-16 text-center text-slate-400" data-testid="observatoire-chargement">
          <i className="bi bi-arrow-repeat animate-spin text-2xl"></i>
          <p className="text-sm mt-2">Chargement des statistiques…</p>
        </div>
      ) : !donnees ? null : donnees.kpis.ventes < 10 ? (
        <section className="bg-white rounded-xl border border-slate-200 p-6 text-center"
                 data-testid="observatoire-trop-peu">
          <i className="bi bi-bar-chart text-2xl text-slate-300"></i>
          <p className="text-sm text-slate-700 mt-2">
            {formatNumberFR(donnees.kpis.ventes)} vente{donnees.kpis.ventes > 1 ? 's' : ''} publiée
            {donnees.kpis.ventes > 1 ? 's' : ''} en {donnees.mois} mois — pas assez de ventes pour
            donner un prix (il en faut au moins 10)
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {donnees.zone_superieure && (
              <button
                type="button"
                onClick={() => changerFiltres({
                  zoneType: donnees.zone_superieure.zone_type,
                  zone: donnees.zone_superieure.zone,
                })}
                data-testid="observatoire-elargir-zone"
                className="h-11 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold"
              >
                Élargir à {donnees.zone_superieure.libelle}
              </button>
            )}
            {donnees.mois !== 36 && (
              <button
                type="button"
                onClick={() => changerFiltres({ mois: 36 })}
                data-testid="observatoire-elargir-periode"
                className="h-11 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold"
              >
                Passer à 36 mois
              </button>
            )}
          </div>
        </section>
      ) : (
        <>
          <ChiffresCles donnees={donnees} portefeuille={portefeuille}
                        activiteChoisie={activiteChoisie} />
          <p className="text-xs text-slate-500" data-testid="observatoire-periode">
            Ventes publiées {donnees.periode?.libelle} ({donnees.mois} mois complets, le mois en
            cours n'est pas compté).
          </p>

          {portefeuille?.concentration && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                 data-testid="observatoire-concentration">
              <p className="text-xs text-slate-600 flex-1">
                Vos {formatNumberFR(portefeuille.concentration.nombre)} fonds sont à{' '}
                {portefeuille.concentration.part} % en {portefeuille.concentration.libelle} : face à
                la France entière, les écarts de prix mêlent aussi la différence entre régions.
              </p>
              <button
                type="button"
                onClick={() => changerFiltres({ zoneType: 'region',
                  zone: portefeuille.concentration.region })}
                data-testid="observatoire-concentration-btn"
                className="h-11 px-4 rounded-lg border text-sm font-semibold shrink-0"
                style={{ borderColor: COULEUR_PORTEFEUILLE, color: COULEUR_PORTEFEUILLE }}
              >
                Comparer sur {portefeuille.concentration.libelle}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GraphiqueVentes donnees={donnees} portefeuilleActif={filtres.portefeuille} />
            <GraphiquePrix donnees={donnees} portefeuille={portefeuille}
                           activiteChoisie={activiteChoisie}
                           libelleActivite={donnees.activite?.libelle} />
            <GraphiqueFamilles donnees={donnees} portefeuille={portefeuille} />
            <GraphiqueTranches donnees={donnees} portefeuille={portefeuille}
                               activiteChoisie={activiteChoisie} />

            <TableauObservatoire
              titre="Par activité"
              legende="Les 12 activités les plus vendues dans la zone, prix médian de vente."
              colonne="Activité"
              lignes={donnees.activites}
              portefeuille={portefeuille?.activites}
              activiteChoisie={activiteChoisie}
              avecStock
              onClic={(ligne) => ligne.cle !== 'non_classe'
                && changerFiltres({ profil: ligne.profil, sousProfil: ligne.cle })}
              testid="observatoire-tableau-activites"
            />

            {donnees.sous_zones.length > 0 && (
              <TableauObservatoire
                titre={`Par ${COLONNE_SOUS_ZONE[donnees.sous_zones[0].type].toLowerCase()}`}
                legende="Un clic sur une ligne applique le filtre de zone."
                colonne={COLONNE_SOUS_ZONE[donnees.sous_zones[0].type]}
                lignes={donnees.sous_zones}
                portefeuille={portefeuille?.sous_zones}
                activiteChoisie={activiteChoisie}
                onClic={(ligne) => changerFiltres({ zoneType: ligne.type, zone: ligne.cle })}
                testid="observatoire-tableau-zones"
              />
            )}
          </div>

          <p className="text-[11px] text-slate-400 text-center pt-2">
            {formatNumberFR(donnees.kpis.ventes)} ventes publiées ·{' '}
            {donnees.kpis.mediane ? `médiane ${formatPriceFR(donnees.kpis.mediane)} · ` : ''}
            {donnees.source}
          </p>
        </>
      )}
    </div>
  );
}
